import { useState, useRef, useMemo } from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { 
  FileSpreadsheet, 
  Upload, 
  Building2, 
  Loader2, 
  Check, 
  Layers, 
  ChevronDown, 
  ChevronUp,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { readExcelFile, parseExcelSheet, readCSVFile } from '../utils/excelParser';
import { CUSTOMERS } from '../utils/constants';
import { useTranslation } from '../../../lib/i18n';

export function ImportDialog({
  open,
  onOpenChange,
  primaryCustomer,
  isMultiCustomer,
  firms,
  selectedFirm,
  user,
  onImportComplete,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importFirmId, setImportFirmId] = useState('');
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  
  // Çoklu sheet seçimi için state
  const [selectedSheets, setSelectedSheets] = useState(new Set());
  const [sheetData, setSheetData] = useState({}); // { sheetName: { data: [], error: null } }
  const [previewSheet, setPreviewSheet] = useState(''); // Önizleme için aktif sheet
  const [showSheetList, setShowSheetList] = useState(true);
  
  // Modal içinde seçilen müşteri tipi
  const [importCustomer, setImportCustomer] = useState('');
  
  // Drag & Drop state
  const [isDragging, setIsDragging] = useState(false);

  const handleClose = () => {
    setExcelWorkbook(null);
    setExcelSheets([]);
    setSelectedSheets(new Set());
    setSheetData({});
    setPreviewSheet('');
    setImportFirmId('');
    setImportCustomer('');
    setShowSheetList(true);
    setIsDragging(false);
    onOpenChange(false);
  };

  // Müşteri değiştiğinde tüm seçili sheet'leri yeniden parse et
  const handleCustomerChange = (newCustomer) => {
    setImportCustomer(newCustomer);
    
    if (excelWorkbook && selectedSheets.size > 0) {
      const newSheetData = {};
      selectedSheets.forEach(sheetName => {
        const { data, error } = parseExcelSheet(excelWorkbook, sheetName, newCustomer);
        newSheetData[sheetName] = { data, error };
      });
      setSheetData(newSheetData);
      
      const totalRows = Object.values(newSheetData).reduce((sum, s) => sum + (s.data?.length || 0), 0);
      if (totalRows > 0) {
        toast.success(`${totalRows} satır okundu (${CUSTOMERS.find(c => c.id === newCustomer)?.name || newCustomer})`);
      }
    }
  };

  // Dosya işleme fonksiyonu (hem file input hem drag-drop için)
  const processFile = async (file) => {
    if (!file) return;
    
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCSV = file.name.endsWith('.csv');
    
    if (!isExcel && !isCSV) {
      toast.error(t('routes.import.unsupportedFormat'));
      return;
    }
    
    try {
      if (isExcel) {
        const { workbook, sheets } = await readExcelFile(file);
        setExcelWorkbook(workbook);
        setExcelSheets(sheets);
        
        // Tüm sheet'leri varsayılan olarak seç
        const allSheets = new Set(sheets);
        setSelectedSheets(allSheets);
        
        // Tüm sheet'leri parse et
        const newSheetData = {};
        sheets.forEach(sheetName => {
          const { data, error } = parseExcelSheet(workbook, sheetName, importCustomer);
          newSheetData[sheetName] = { data, error };
        });
        setSheetData(newSheetData);
        
        // İlk sheet'i önizleme için seç
        if (sheets.length > 0) {
          setPreviewSheet(sheets[0]);
        }
        
        const totalRows = Object.values(newSheetData).reduce((sum, s) => sum + (s.data?.length || 0), 0);
        toast.success(`${t('routes.import.excelLoaded')}: ${sheets.length} ${t('routes.import.sheetsCount')}, ${totalRows} ${t('routes.import.totalRows')}`);
      } else {
        const { data, error } = await readCSVFile(file);
        if (error) {
          toast.error(`${t('routes.import.fileReadError')}: ` + error);
        } else {
          setSheetData({ 'CSV': { data, error: null } });
          setSelectedSheets(new Set(['CSV']));
          setPreviewSheet('CSV');
          toast.success(`${data.length} ${t('routes.import.rowsRead')}`);
        }
      }
    } catch (err) {
      console.error('File read error:', err);
      toast.error(`${t('routes.import.fileReadError')}: ` + err.message);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    await processFile(file);
    event.target.value = '';
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  // Sheet seçimini toggle et
  const toggleSheetSelection = (sheetName) => {
    const newSelected = new Set(selectedSheets);
    if (newSelected.has(sheetName)) {
      newSelected.delete(sheetName);
    } else {
      newSelected.add(sheetName);
      // Eğer henüz parse edilmemişse parse et
      if (!sheetData[sheetName] && excelWorkbook) {
        const { data, error } = parseExcelSheet(excelWorkbook, sheetName, importCustomer);
        setSheetData(prev => ({ ...prev, [sheetName]: { data, error } }));
      }
    }
    setSelectedSheets(newSelected);
    
    // Eğer önizleme sheet'i seçilmemişse, seçili bir sheet'e geç
    if (!newSelected.has(previewSheet) && newSelected.size > 0) {
      setPreviewSheet([...newSelected][0]);
    }
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    if (selectedSheets.size === excelSheets.length) {
      setSelectedSheets(new Set());
      setPreviewSheet('');
    } else {
      const allSheets = new Set(excelSheets);
      setSelectedSheets(allSheets);
      
      // Henüz parse edilmemiş sheet'leri parse et
      const newSheetData = { ...sheetData };
      excelSheets.forEach(sheetName => {
        if (!newSheetData[sheetName] && excelWorkbook) {
          const { data, error } = parseExcelSheet(excelWorkbook, sheetName, importCustomer);
          newSheetData[sheetName] = { data, error };
        }
      });
      setSheetData(newSheetData);
      
      if (!previewSheet && excelSheets.length > 0) {
        setPreviewSheet(excelSheets[0]);
      }
    }
  };

  // Toplam seçili satır sayısı
  const totalSelectedRows = useMemo(() => {
    return [...selectedSheets].reduce((sum, sheetName) => {
      return sum + (sheetData[sheetName]?.data?.length || 0);
    }, 0);
  }, [selectedSheets, sheetData]);

  // Sheet bazlı özet
  const sheetSummary = useMemo(() => {
    return excelSheets.map(sheetName => ({
      name: sheetName,
      selected: selectedSheets.has(sheetName),
      rows: sheetData[sheetName]?.data?.length || 0,
      error: sheetData[sheetName]?.error
    }));
  }, [excelSheets, selectedSheets, sheetData]);

  const handleImport = async () => {
    if (!importCustomer) {
      toast.error(t('routes.import.pleaseSelectCustomer'));
      return;
    }
    
    if (totalSelectedRows === 0) {
      toast.error(t('routes.import.noDataToImport'));
      return;
    }
    
    let firmIdToUse = importFirmId || selectedFirm || user?.firm_id;
    
    if (user?.role === 'FIRM_ADMIN') {
      firmIdToUse = user.firm_id;
    }
    
    if (!firmIdToUse) {
      toast.error(t('routes.import.selectFirm'));
      return;
    }
    
    try {
      setImporting(true);
      
      // Tüm seçili sheet'lerdeki verileri birleştir
      const allData = [];
      selectedSheets.forEach(sheetName => {
        const data = sheetData[sheetName]?.data || [];
        allData.push(...data);
      });
      
      const formData = new FormData();
      formData.append('customer', importCustomer);
      formData.append('firm_id', firmIdToUse);
      formData.append('data', JSON.stringify(allData));
      
      const response = await api.post('/routes/import', formData);
      if (response.data.success) {
        const { imported, duplicate_count, duplicates } = response.data.data || {};
        
        // Show success message
        toast.success(response.data.message);
        
        // Show duplicates warning if any
        if (duplicate_count > 0) {
          const duplicatesList = duplicates?.slice(0, 5).map(d => d.info).join('\n') || '';
          const moreText = duplicate_count > 5 ? `\n... ${t('routes.import.andMore')} ${duplicate_count - 5} ${t('routes.records')}` : '';
          toast.warning(
            `${duplicate_count} ${t('routes.import.duplicateWarning')}:\n${duplicatesList}${moreText}`,
            { duration: 8000 }
          );
        }
        
        handleClose();
        onImportComplete();
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = t('routes.import.error');
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  // Müşteri adını al
  const getCustomerName = (customerId) => {
    return CUSTOMERS.find(c => c.id === customerId)?.name || customerId;
  };

  // Önizleme verisi
  const previewData = sheetData[previewSheet]?.data || [];

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange(true) : handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            {t('routes.import.title')}
            {excelSheets.length > 1 && (
              <Badge variant="secondary" className="ml-2">
                <Layers className="h-3 w-3 mr-1" />
                {t('routes.import.multiSheetSupport')}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Müşteri Seçimi - Modal içinde */}
          <div className={`p-3 rounded-lg border transition-colors ${
            !importCustomer 
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700' 
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
          }`}>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
              {t('routes.import.customerType')} *
              {!importCustomer && (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-normal">
                  ({t('routes.import.pleaseSelectCustomer')})
                </span>
              )}
            </label>
            <div className="flex flex-wrap gap-2">
              {CUSTOMERS.map(customer => (
                <Button
                  key={customer.id}
                  variant={importCustomer === customer.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCustomerChange(customer.id)}
                  className={importCustomer === customer.id ? '' : customer.color}
                >
                  {importCustomer === customer.id && <Check className="h-3 w-3 mr-1" />}
                  {customer.name}
                </Button>
              ))}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              {t('routes.import.columnMapping')}
            </p>
          </div>

          {/* Firma Selection for Super Admin */}
          {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{t('routes.import.firmLabel')}</span>
              <Select 
                value={importFirmId || selectedFirm || ''} 
                onValueChange={(v) => setImportFirmId(v)}
              >
                <SelectTrigger className="w-64">
                  <SelectValue placeholder={`${t('routes.import.selectFirm')} *`} />
                </SelectTrigger>
                <SelectContent>
                  {firms.map(firm => (
                    <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!importFirmId && !selectedFirm && (
                <span className="text-xs text-red-500">{t('routes.import.required')}</span>
              )}
            </div>
          )}

          {/* File Upload - Drag & Drop Zone */}
          <div 
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 cursor-pointer
              ${isDragging 
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 scale-[1.02]' 
                : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }
            `}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileSelect}
            />
            <div className={`transition-transform duration-200 ${isDragging ? 'scale-110' : ''}`}>
              <FileSpreadsheet className={`h-12 w-12 mx-auto mb-3 ${isDragging ? 'text-emerald-500' : 'text-slate-400'}`} />
            </div>
            {isDragging ? (
              <p className="text-emerald-600 dark:text-emerald-400 font-medium mb-2">
                {t('routes.import.dropFileHere')}
              </p>
            ) : (
              <>
                <p className="text-slate-600 dark:text-slate-300 mb-2">
                  {t('routes.import.dragDropFile')}
                </p>
                <p className="text-xs text-slate-500 mb-3">
                  {t('routes.import.fileFormats')}
                </p>
              </>
            )}
            <Button 
              variant="outline" 
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              className={isDragging ? 'opacity-0' : ''}
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('routes.import.selectFile')}
            </Button>
          </div>

          {/* Çoklu Sheet Seçimi */}
          {excelSheets.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              {/* Header */}
              <div 
                className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-b cursor-pointer"
                onClick={() => setShowSheetList(!showSheetList)}
              >
                <div className="flex items-center gap-3">
                  <Layers className="h-5 w-5 text-emerald-600" />
                  <span className="font-medium text-emerald-800 dark:text-emerald-200">
                    {t('routes.import.sheetSelection')}
                  </span>
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                    {selectedSheets.size} / {excelSheets.length} {t('routes.import.sheetsSelected')}
                  </Badge>
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                    {totalSelectedRows} {t('routes.import.rows')}
                  </Badge>
                </div>
                <Button variant="ghost" size="sm">
                  {showSheetList ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>

              {/* Sheet List */}
              {showSheetList && (
                <div className="p-3 space-y-2 max-h-48 overflow-y-auto bg-white dark:bg-slate-900">
                  {/* Tümünü Seç */}
                  <div className="flex items-center justify-between pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedSheets.size === excelSheets.length}
                        onCheckedChange={toggleSelectAll}
                      />
                      <span className="text-sm font-medium">{t('routes.import.selectDeselectAll')}</span>
                    </div>
                  </div>

                  {/* Sheet Items */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {sheetSummary.map(sheet => (
                      <div
                        key={sheet.name}
                        className={`
                          flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all
                          ${sheet.selected 
                            ? 'bg-emerald-50 border-emerald-300 dark:bg-emerald-900/30 dark:border-emerald-600' 
                            : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-100'
                          }
                        `}
                        onClick={() => toggleSheetSelection(sheet.name)}
                      >
                        <Checkbox
                          checked={sheet.selected}
                          onCheckedChange={() => toggleSheetSelection(sheet.name)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{sheet.name}</p>
                          <p className={`text-xs ${sheet.rows > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {sheet.rows > 0 ? `${sheet.rows} ${t('routes.import.rows')}` : t('routes.import.noData')}
                          </p>
                        </div>
                        {sheet.selected && sheet.rows > 0 && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        )}
                        {sheet.error && (
                          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Önizleme Sheet Seçici */}
          {selectedSheets.size > 0 && (
            <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <Eye className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-600 dark:text-slate-300">{t('routes.import.preview')}:</span>
              <div className="flex flex-wrap gap-1">
                {[...selectedSheets].map(sheetName => (
                  <Button
                    key={sheetName}
                    variant={previewSheet === sheetName ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setPreviewSheet(sheetName)}
                  >
                    {sheetName}
                    <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">
                      {sheetData[sheetName]?.data?.length || 0}
                    </Badge>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Preview Table */}
          {previewData.length > 0 && (
            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                {t('routes.import.preview')}: {previewSheet}
                <Badge variant="outline">{previewData.length} {t('routes.import.rows')}</Badge>
                <Badge className={`${CUSTOMERS.find(c => c.id === importCustomer)?.color || ''}`}>
                  {getCustomerName(importCustomer)}
                </Badge>
              </h4>
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-100 dark:bg-slate-700">
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">{t('routes.date')}</th>
                      {(importCustomer === 'fedex' || importCustomer === 'dhl' || importCustomer === 'bpost') && <th className="text-left py-2 px-2">{t('routes.routeNumber')}</th>}
                      {(importCustomer === 'getax' || importCustomer === 'express') && <th className="text-left py-2 px-2">{t('routes.routeType')}</th>}
                      {importCustomer === 'mover' && <th className="text-left py-2 px-2">{t('routes.rit')}</th>}
                      <th className="text-left py-2 px-2">{t('routes.licensePlate')}</th>
                      <th className="text-left py-2 px-2">{t('routes.driver')}</th>
                      {importCustomer === 'mover' && <th className="text-left py-2 px-2">{t('routes.copilot')}</th>}
                      {(importCustomer === 'fedex' || importCustomer === 'dhl' || importCustomer === 'bpost') && <th className="text-left py-2 px-2">{t('routes.kmStart')}</th>}
                      {(importCustomer === 'fedex' || importCustomer === 'dhl' || importCustomer === 'bpost') && <th className="text-left py-2 px-2">{t('routes.kmEnd')}</th>}
                      {importCustomer === 'bpost' && <th className="text-left py-2 px-2">Start Kost</th>}
                      <th className="text-left py-2 px-2">{t('routes.startTime')}</th>
                      <th className="text-left py-2 px-2">{t('routes.endTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-100 dark:hover:bg-slate-700">
                        <td className="py-1 px-2">{row.date}</td>
                        {(importCustomer === 'fedex' || importCustomer === 'dhl' || importCustomer === 'bpost') && <td className="py-1 px-2">{row.route_number}</td>}
                        {(importCustomer === 'getax' || importCustomer === 'express') && <td className="py-1 px-2">{row.route_type}</td>}
                        {importCustomer === 'mover' && <td className="py-1 px-2">{row.plaats}</td>}
                        <td className="py-1 px-2">{row.license_plate}</td>
                        <td className="py-1 px-2">{row.driver_name}</td>
                        {importCustomer === 'mover' && <td className="py-1 px-2 text-slate-500">{row.copilot_name || '-'}</td>}
                        {(importCustomer === 'fedex' || importCustomer === 'dhl' || importCustomer === 'bpost') && <td className="py-1 px-2">{row.km_start}</td>}
                        {(importCustomer === 'fedex' || importCustomer === 'dhl' || importCustomer === 'bpost') && <td className="py-1 px-2">{row.km_end}</td>}
                        {importCustomer === 'bpost' && <td className="py-1 px-2">{row.start_cost || '-'}</td>}
                        <td className="py-1 px-2">{row.start_time}</td>
                        <td className="py-1 px-2">{row.end_time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 10 && (
                  <p className="text-center text-slate-500 dark:text-slate-400 text-xs mt-2">
                    ... {t('routes.import.andMore')} {previewData.length - 10} {t('routes.import.moreRows')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Özet Bilgisi */}
          {selectedSheets.size > 0 && totalSelectedRows > 0 && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-medium text-emerald-800 dark:text-emerald-200">
                    {t('routes.import.toImport')}:
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-emerald-600">
                    {selectedSheets.size} {t('routes.import.sheet')}
                  </Badge>
                  <Badge className="bg-emerald-600">
                    {totalSelectedRows} {t('routes.import.rows')}
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                {t('routes.import.selectedSheets')}: {[...selectedSheets].join(', ')}
              </p>
            </div>
          )}

          {/* Help Text - Sadece müşteri seçildiğinde göster */}
          {importCustomer && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                {importCustomer === 'mover' ? t('routes.import.moverColumns') : 
                 importCustomer === 'fedex' ? t('routes.import.fedexColumns') : 
                 importCustomer === 'dhl' ? 'DHL Excel Sütunları:' :
                 importCustomer === 'bpost' ? 'Bpost Excel Sütunları:' :
                 importCustomer === 'express' ? t('routes.import.expressColumns') : t('routes.import.getaxColumns')}
              </p>
              {importCustomer === 'bpost' ? (
                <>
                  <p className="text-blue-600 dark:text-blue-300">
                    Datum, Route Nummer, Nrplaat, Naam Chauffeur, Start Kost, Startuur, Einduur, Begin Km, Eind KM
                  </p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    Opsiyonel: Werkuur Start, Aantal Uren, Totaal Km, Kost Werktijd, Totaal excl BTW
                  </p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    Not: Totaal excl BTW = Start Kost olarak hesaplanır
                  </p>
                </>
              ) : importCustomer === 'mover' ? (
                <>
                  <p className="text-blue-600 dark:text-blue-300">
                    Datum, Rit (Luik/Hasselt/Extra/vb.), Nrplaat, Naam Chauffeur, Naam Copilot, Startuur, Einduur, Pauze
                  </p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    {t('routes.import.optionalFields')}: Uurprijs ({t('routes.import.defaultRate')} €78), {t('routes.import.startCost')}, {t('routes.import.notes')}
                  </p>
                </>
              ) : importCustomer === 'fedex' ? (
                <>
                  <p className="text-blue-600 dark:text-blue-300">
                    Datum, Route Nummer, Nrplaat, Naam Chauffeur, KM Start, KM Einde, Startuur, Einduur, Pauze
                  </p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    {t('routes.import.optionalFields')}: Uurprijs ({t('routes.import.defaultRate')} €40), {t('routes.import.startCost')}, {t('routes.import.notes')}
                  </p>
                </>
              ) : importCustomer === 'dhl' ? (
                <>
                  <p className="text-blue-600 dark:text-blue-300">
                    Datum, Route Nummer, Nrplaat, Naam Chauffeur, KM Start, KM Einde, Stop Prijs, Stops, Startuur, Einduur, Pauze
                  </p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    Opsiyonel: KM Aantal, Aantal Uren
                  </p>
                </>
              ) : importCustomer === 'express' ? (
                <>
                  <p className="text-blue-600 dark:text-blue-300">
                    Datum, Tur Tipi, Nrplaat, Naam Chauffeur, Startuur, Einduur, Pauze
                  </p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    {t('routes.import.optionalFields')}: Uurprijs ({t('routes.import.defaultRate')} €325), {t('routes.import.startCost')}, {t('routes.import.notes')}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-blue-600 dark:text-blue-300">Datum, Tur Tipi (veya Route Nummer), Nrplaat, Naam Chauffeur, Startuur, Einduur, Pauze</p>
                  <p className="text-blue-500 dark:text-blue-400 text-xs mt-1">
                    {t('routes.import.optionalFields')}: Uurprijs ({t('routes.import.defaultRate')} €325), {t('routes.import.startCost')}, {t('routes.import.notes')}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('routes.cancel')}
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={importing || totalSelectedRows === 0 || !importCustomer || ((user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && !importFirmId && !selectedFirm)}
            className="min-w-[200px]"
          >
            {importing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {!importCustomer 
              ? t('routes.import.selectCustomerType')
              : selectedSheets.size > 1 
                ? `${selectedSheets.size} ${t('routes.import.importSheets')} / ${totalSelectedRows} ${t('routes.import.importRecords')}`
                : `${totalSelectedRows} ${t('routes.import.importRecords')}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
